import React, { CSSProperties } from 'react';
import '../utilities/utils.css';
import { PatternFormat } from 'react-number-format';

const sizeTokens = {
    NORMAL: {
        inputPadding: '1rem',
        inputFontSize: '1rem',
        labelFontSize: '1rem',
        gap: '0.5rem',
        borderRadius: '0.5rem',
        buttonSize: '1.5rem',
        buttonOffset: '1rem',
    },
    SMALL: {
        inputPadding: 'var(--space-xs) var(--space-s)',
        inputFontSize: 'var(--font-size-s)',
        labelFontSize: 'var(--font-size-s)',
        gap: 'var(--space-xxs)',
        borderRadius: 'var(--space-xs)',
        buttonSize: '1.2rem',
        buttonOffset: 'var(--space-xs)',
    },
};

const buildClasses = (size: 'SMALL' | 'NORMAL'): { [key: string]: CSSProperties } => {
    const tokens = sizeTokens[size];
    return {
        'div-root': {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: tokens.gap,
        },
        'input-root': {
            padding: tokens.inputPadding,
            color: 'var(--textarea-text-color)',
            width: '100%',
            border: '1px solid var(--textarea-border-color)',
            fontSize: tokens.inputFontSize,
            fontFamily: 'var(--ion-font-family)',
            borderRadius: tokens.borderRadius,
            boxSizing: 'border-box',
            position: 'relative',
        },
        'input-button': {
            width: tokens.buttonSize,
            height: tokens.buttonSize,
            backgroundColor: 'var(--color-primary)',
            border: 'none',
            position: 'absolute',
            right: tokens.buttonOffset,
            top: tokens.buttonOffset,
            padding: '0px',
        },
        'label-root': {
            color: 'var(--color-primary)',
            textAlign: 'left',
            width: '100%',
            paddingLeft: '.5rem',
            fontSize: tokens.labelFontSize,
            fontWeight: '600',
            fontFamily: 'var(--ion-font-family)',
        },
        'desc-root': {
            color: 'var(--color-primary)',
            textAlign: 'left',
            width: '100%',
            margin: '0',
            paddingLeft: '.5rem',
            fontSize: '0.8rem',
            fontWeight: '400',
        },
        container: {
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
    };
};

interface Props {
    pink?: boolean;
    value?: string;
    size?: 'SMALL' | 'NORMAL';
    sx?: CSSProperties;
    sxLabel?: CSSProperties;
    sxDesc?: CSSProperties;
    sxRoot?: CSSProperties;
    sxBtnClick?: CSSProperties;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
    btnClick?: () => void;
    placeholder?: string;
    label?: string;
    labelStar?: boolean;
    labelPlaceholder?: boolean;
    desc?: string;
    error?: boolean;
    imgIntoInput?: string;
    type?: string;
    disabled?: boolean;
    ref?: React.LegacyRef<HTMLInputElement> | undefined;
    multiple?: boolean;
    accept?: string;
    maxLength?: number;
    format?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    autoComplete?: string;
    lpIgnore?: boolean;
}

export function Input({
    pink,
    value,
    size = 'NORMAL',
    sx,
    sxLabel,
    sxDesc,
    sxRoot,
    sxBtnClick,
    onChange,
    onBlur,
    btnClick,
    placeholder,
    label,
    labelStar,
    labelPlaceholder,
    desc,
    error,
    imgIntoInput,
    type,
    disabled,
    ref,
    multiple,
    accept,
    maxLength,
    format,
    onKeyDown,
    autoComplete,
    lpIgnore
}: Props): JSX.Element {
    const classes = buildClasses(size);
    const [inputFocus, setInputFocus] = React.useState(false);
    return !labelPlaceholder ? (
        <div style={{ ...classes['div-root'], ...sxRoot }}>
            {label !== undefined && (
                <label
                    style={{
                        ...classes['label-root'],
                        ...(pink && inputFocus ? { color: 'var(--button-bg-color-pink)' } : {}),
                        ...sxLabel
                    }}
                    htmlFor={label}>
                    {label}
                    {labelStar && <span style={{ color: 'var(--color-error)' }}>&nbsp;*</span>}
                </label>
            )}
            {desc !== undefined && <p style={{ ...classes['desc-root'], ...sxDesc }}>{desc}</p>}
            <div style={{ position: 'relative', width: '100%' }}>
                {format ? (
                    <PatternFormat
                        format={format}
                        style={{
                            ...classes['input-root'],
                            ...{
                                cursor: disabled ? 'not-allowed' : '',
                                backgroundColor: disabled
                                    ? 'var(--input-bg-color-disabled)'
                                    : 'var(--color-white)',
                                paddingRight: imgIntoInput ? '3rem' : '1rem'
                            },
                            ...(error ? { borderColor: 'var(--color-error)' } : {}),
                            ...sx
                        }}
                        name={label}
                        value={value}
                        placeholder={placeholder}
                        onChange={(input): void => onChange && onChange(input.target.value)}
                    />
                ) : (
                    <input
                        onBlurCapture={(e) => {
                            if (onBlur) {
                                onBlur(e.target.value);
                            }
                        }}
                        disabled={disabled}
                        type={type}
                        name={label}
                        onFocus={() => setInputFocus(true)}
                        onBlur={() => setInputFocus(false)}
                        className={pink ? 'input-pink' : ''}
                        maxLength={maxLength}
                        style={{
                            ...classes['input-root'],
                            ...{
                                cursor: disabled ? 'not-allowed' : '',
                                backgroundColor: disabled
                                    ? 'var(--input-bg-color-disabled)'
                                    : 'var(--color-white)',
                                paddingRight: imgIntoInput ? '3rem' : '1rem'
                            },
                            ...(error ? { borderColor: 'var(--color-error)' } : {}),
                            ...sx
                        }}
                        value={value}
                        placeholder={placeholder}
                        onKeyDown={(e) => onKeyDown && onKeyDown(e)}

                        autoComplete={autoComplete}
                        data-lpignore={lpIgnore || undefined}
                        onChange={(e) => {
                            if (onChange) {
                                onChange(e.target.value);
                            }
                        }}
                        ref={ref}
                        multiple={multiple}
                        accept={accept}
                    />
                )}
                {btnClick && (
                    <input
                        disabled={disabled}
                        type="button"
                        value=""
                        onClick={btnClick}
                        style={{
                            ...classes['input-button'],
                            ...{ cursor: disabled ? 'not-allowed' : 'pointer' },
                            ...sxBtnClick,
                            background: `transparent url('${imgIntoInput}') center center  no-repeat`
                        }}
                    />
                )}
            </div>
        </div>
    ) : (
        <div className="container" style={{ fontFamily: 'var(--ion-font-family)', ...sxRoot }}>
            <div className="material-textfield">
                <input
                    placeholder={placeholder ?? ' '}
                    disabled={disabled}
                    type={type}
                    name={label}
                    onFocus={() => setInputFocus(true)}
                    onBlur={() => setInputFocus(false)}
                    style={{
                        backgroundColor: disabled
                            ? 'var(--input-bg-color-disabled)'
                            : 'var(--color-white)',
                        fontFamily: 'var(--ion-font-family)'
                    }}
                    value={value}
                    maxLength={maxLength}
                    onChange={(e) => {
                        if (onChange) {
                            onChange(e.target.value);
                        }
                    }}
                    ref={ref}
                    multiple={multiple}
                    accept={accept}
                />
                <label>{label}</label>
            </div>
        </div>
    );
}
